import React, { useEffect, useState } from "react"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
} from "reactstrap"
import { FaPlus } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import { Switch } from "antd"
import Select from "react-select"
import { customStyles } from "utils/SelectStyles"

const ProjectModal = ({
  open,
  toggle,
  selectedProject,
  setSelectedProject,
  viewOnly,
  onSubmit,
  staffList = [],
}) => {
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setErrors({})
    // Initialize permissions if not present
    if (open && !selectedProject?.permissions) {
      setSelectedProject(prev => ({ ...prev, permissions: [] }))
    }
  }, [open])

  const handleChange = (field, value) => {
    setSelectedProject(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: "" }))
  }

  // ── Assigned Users Helpers ─────────────────────────────────────
  const permissions = selectedProject?.permissions || []

  const addUserRow = () => {
    const newRow = { userId: "", view: 0, edit: 0, delete: 0 }
    handleChange("permissions", [...permissions, newRow])
  }

  const removeUserRow = index => {
    const updated = permissions.filter((_, i) => i !== index)
    handleChange("permissions", updated)
  }

  const updateUserRow = (index, field, value) => {
    const updated = permissions.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    )
    handleChange("permissions", updated)
    // clear row-level error
    setErrors(prev => {
      const rowErrors = { ...(prev.permissions || {}) }
      delete rowErrors[index]
      return { ...prev, permissions: rowErrors }
    })
  }

  // already selected user IDs (to avoid duplicates)
  const selectedUserIds = permissions.map(r => String(r.userId))

  // ── Validation ─────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {}

    if (!selectedProject?.projectName?.trim())
      newErrors.projectName = "Department name is required"
    if (permissions.length === 0) {
      newErrors.permissions = { general: "Assign at least one user" }
    } else {
      const rowErrors = {}
      permissions.forEach((row, i) => {
        if (!row.userId) rowErrors[i] = "Please select a user"
      })
      if (Object.keys(rowErrors).length > 0) newErrors.permissions = rowErrors
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    onSubmit()
  }

  const renderError = field =>
    errors[field] && (
      <div className="invalid-feedback d-block">{errors[field]}</div>
    )

  const invalid = field => (errors[field] ? "is-invalid" : "")

  return (
    <Modal isOpen={open} centered size="xl" toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {viewOnly
          ? "View Department"
          : selectedProject?.id
          ? "Edit Department"
          : "Add Department"}
      </ModalHeader>

      <ModalBody>
        <Form>
          {/* ── BASIC DETAILS ─────────────────────────────────── */}
          <Row>
            {/* Name */}
            <Col md={6}>
              <FormGroup>
                <Label>
                  Department Name <span className="text-danger">*</span>
                </Label>
                <Input
                  value={selectedProject?.projectName || ""}
                  onChange={e => handleChange("projectName", e.target.value)}
                  disabled={viewOnly}
                  placeholder="Enter project name"
                  className={`form-control rounded-4 ${invalid("projectName")}`}
                />
                {renderError("projectName")}
              </FormGroup>
            </Col>

            {/* Location */}
            <Col md={6}>
              <FormGroup>
                <Label>Location</Label>
                <Input
                  value={selectedProject?.location || ""}
                  onChange={e => handleChange("location", e.target.value)}
                  disabled={viewOnly}
                  placeholder="Enter project location"
                  className={`form-control rounded-4 `}
                />
              </FormGroup>
            </Col>

            {/* Description */}
            <Col md={12}>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  rows={3}
                  value={selectedProject?.description || ""}
                  onChange={e => handleChange("description", e.target.value)}
                  disabled={viewOnly}
                  placeholder="Enter project description"
                  className={`form-control rounded-4 `}
                />
              </FormGroup>
            </Col>
          </Row>

          {/* ── ASSIGNED USERS + PERMISSIONS ──────────────────── */}
          <div className="mt-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold mb-0">
                Assigned Users & Permissions{" "}
                <span className="text-danger">*</span>
              </h6>
              {!viewOnly && (
                <Button
                  className="btn-blue rounded-4 px-3"
                  onClick={addUserRow}
                >
                  <FaPlus className="me-1" size={12} />
                  Add User
                </Button>
              )}
            </div>

            {/* General error for no users */}
            {errors?.permissions?.general && (
              <div className="invalid-feedback d-block mb-2">
                {errors.permissions.general}
              </div>
            )}

            <Table bordered responsive className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "35%" }}>User</th>
                  <th className="text-center" style={{ width: "15%" }}>
                    View Global
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    View / Download
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    Edit
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    Delete
                  </th>
                  {!viewOnly && (
                    <th className="text-center" style={{ width: "10%" }}>
                      Remove
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {permissions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={viewOnly ? 4 : 5}
                      className="text-center text-muted py-3"
                    >
                      No users assigned yet.{" "}
                      {!viewOnly && (
                        <span
                          className="text-primary"
                          style={{ cursor: "pointer" }}
                          onClick={addUserRow}
                        >
                          + Add a user
                        </span>
                      )}
                    </td>
                  </tr>
                ) : (
                  permissions.map((row, index) => {
                    const rowError = errors?.permissions?.[index]

                    // staff options: exclude already-selected users except current row
                    const availableStaff = staffList.filter(
                      s =>
                        !selectedUserIds.includes(String(s.id)) ||
                        String(s.id) === String(row.userId)
                    )

                    return (
                      <tr key={index}>
                        {/* User Select */}
                        <td>
                          {viewOnly ? (
                            <span>
                              {staffList.find(
                                s => String(s.id) === String(row.userId)
                              )?.name || `User #${row.userId}`}
                            </span>
                          ) : (
                            <>
                              <Select
                                value={
                                  row.userId
                                    ? {
                                        value: row.userId,
                                        label: availableStaff.find(
                                          s => s.id === row.userId
                                        )?.name,
                                      }
                                    : null
                                }
                                onChange={selectedOption =>
                                  updateUserRow(
                                    index,
                                    "userId",
                                    selectedOption ? selectedOption.value : ""
                                  )
                                }
                                options={availableStaff.map(s => ({
                                  value: s.id,
                                  label: s.name,
                                }))}
                                placeholder="Select User"
                                styles={{
                                  ...customStyles,
                                  menuPortal: base => ({
                                    ...base,
                                    zIndex: 9999,
                                  }), // ensures dropdown is on top
                                }}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                              />
                              {rowError && (
                                <div className="invalid-feedback d-block">
                                  {rowError}
                                </div>
                              )}
                            </>
                          )}
                        </td>

                        {/* View Global*/}
                        <td className="text-center align-middle">
                          <Switch
                            checked={row.viewglobal === 1}
                            onChange={checked =>
                              updateUserRow(
                                index,
                                "viewglobal",
                                checked ? 1 : 0
                              )
                            }
                            disabled={viewOnly}
                          />
                        </td>

                        {/* View */}
                        <td className="text-center align-middle">
                          <Switch
                            checked={row.view === 1}
                            onChange={checked =>
                              updateUserRow(index, "view", checked ? 1 : 0)
                            }
                            disabled={viewOnly}
                          />
                        </td>

                        {/* Edit */}
                        <td className="text-center align-middle">
                          <Switch
                            checked={row.edit === 1}
                            onChange={checked =>
                              updateUserRow(index, "edit", checked ? 1 : 0)
                            }
                            disabled={viewOnly}
                          />
                        </td>

                        {/* Delete */}
                        <td className="text-center align-middle">
                          <Switch
                            checked={row.delete === 1}
                            onChange={checked =>
                              updateUserRow(index, "delete", checked ? 1 : 0)
                            }
                            disabled={viewOnly}
                          />
                        </td>

                        {/* Remove Row */}
                        {!viewOnly && (
                          <td className="text-center align-middle">
                            <Button
                              size="sm"
                              className="bg-danger text-white border-0 shadow-none"
                              onClick={() => removeUserRow(index)}
                            >
                              <MdDelete size={16} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </Table>

            {/* Preview of stored structure */}
            {/* {permissions.length > 0 && !viewOnly && (
              <div className="mt-2">
                <small className="text-muted">
                  <strong>Stored as: </strong>
                  {JSON.stringify(
                    permissions
                      .filter(r => r.userId)
                      .map(r => ({
                        userId: Number(r.userId),
                        view: r.view,
                        edit: r.edit,
                        delete: r.delete,
                      }))
                  )}
                </small>
              </div>
            )} */}
          </div>
        </Form>
      </ModalBody>

      {!viewOnly && (
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            {selectedProject?.id ? "Update" : "Submit"}
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      )}
    </Modal>
  )
}

export default ProjectModal
